import {useQuery} from '@apollo/client'
import {Alert, Spinner, Table} from 'react-bootstrap'
import {
  GET_MEMBER_COMMISSION_REPORTS_DETAILS,
  GET_POLICY_COMMISSION_REPORTS_DETAILS,
} from '../../../gql/queries/commissionReportsDetailsApiQueries'
import {parseId} from '../../../helpers'
import {useNavigate} from 'react-router-dom'

const MONTH_HEADERS = [
  'Undefined',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

type CommissionCellStatus = 'paid' | 'chargeback' | 'notPaid'

type CommissionCell = {
  applicants: number
  status: CommissionCellStatus
}

type CommissionRow = {
  reportId: string
  reportName: string
  reportMonth: number
  reportYear: number
  client?: string | null
  memberId?: string | null
  cells: Record<number, CommissionCell>
}

type CommissionDetailNode = {
  id: string
  commissionType: string
  status: string
  members: number
  effectiveMonth: number
  effectiveYear?: number | null
  client?: string | null
  memberId?: string | null
  commissionReport: {
    id: string
    name: string
    effectiveMonth: number
    effectiveYear: number
    status: string
    carrier?: {
      id: string
      name?: string | null
    } | null
  }
}

type CommissionDetailsResponse = {
  commissionReportsDetailsFiltered: CommissionDetailNode[]
}

type CommissionDetailsByMemberResponse = {
  allCommissionReportsDetails: {
    edges: Array<{
      node: CommissionDetailNode
    }>
  }
}

const STATUS_PRIORITY: Record<CommissionCellStatus, number> = {
  paid: 1,
  notPaid: 2,
  chargeback: 3,
}

const CELL_ICON: Record<CommissionCellStatus, string> = {
  paid: 'bi bi-check-circle-fill text-success',
  notPaid: 'bi bi-x-circle-fill text-danger',
  chargeback: 'bi bi-dash-circle-fill text-danger',
}

const resolveDetailStatus = (commissionType: string, status: string): CommissionCellStatus => {
  if (commissionType === 'CHARGEBACK') {
    return 'chargeback'
  }

  if (status === 'NOT_ASSIGNED') {
    return 'notPaid'
  }

  return 'paid'
}

const normalizeMonth = (month?: number | null) => (month && month >= 1 && month <= 12 ? month : 0)

const mergeCell = (
  row: CommissionRow,
  month: number,
  applicants: number,
  status: CommissionCellStatus
) => {
  const current = row.cells[month]
  if (!current) {
    row.cells[month] = {applicants, status}
    return
  }

  // Caso especial: si existe un registro pagado y otro no asignado en el mismo
  // reporte/mes, el pagado prevalece y el no asignado no debe sumarse.
  if (current.status === 'paid' && status === 'notPaid') {
    return
  }

  if (current.status === 'notPaid' && status === 'paid') {
    row.cells[month] = {applicants, status}
    return
  }

  current.applicants += applicants
  if (STATUS_PRIORITY[status] > STATUS_PRIORITY[current.status]) {
    current.status = status
  }
}

const formatTotalClassName = (cell?: CommissionCell) => {
  if (!cell) return 'text-muted'
  if (cell.status === 'chargeback' || cell.status === 'notPaid') return 'text-danger'
  return 'text-success'
}

const CellContent = ({cell}: {cell?: CommissionCell}) => {
  if (!cell) return <span className='text-muted'>-</span>

  return (
    <div className='d-inline-flex align-items-center gap-1'>
      <i className={CELL_ICON[cell.status]} style={{fontSize: '1.2rem', fontWeight: 700}} />
      <span>{cell.applicants}</span>
    </div>
  )
}

const getEffectiveYear = (effectiveDate?: string | Date | null) => {
  if (!effectiveDate) return null

  if (typeof effectiveDate === 'string') {
    const isoYearMatch = effectiveDate.match(/^(\d{4})-\d{2}-\d{2}/)
    if (isoYearMatch) {
      return Number(isoYearMatch[1])
    }

    const slashYearMatch = effectiveDate.match(/^\d{2}\/\d{2}\/(\d{4})$/)
    if (slashYearMatch) {
      return Number(slashYearMatch[1])
    }
  }

  const parsed = new Date(effectiveDate)
  return Number.isNaN(parsed.getTime()) ? null : parsed.getUTCFullYear()
}

const normalizeCarrierName = (value?: string | null) =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .trim()

const carriersLookCompatible = (policyCarrierName?: string | null, detailCarrierName?: string | null) => {
  const normalizedPolicyCarrier = normalizeCarrierName(policyCarrierName)
  const normalizedDetailCarrier = normalizeCarrierName(detailCarrierName)

  if (!normalizedPolicyCarrier || !normalizedDetailCarrier) return false

  return (
    normalizedPolicyCarrier === normalizedDetailCarrier ||
    normalizedPolicyCarrier.includes(normalizedDetailCarrier) ||
    normalizedDetailCarrier.includes(normalizedPolicyCarrier)
  )
}

export const CommissionsTab = ({
  policyId,
  effectiveDate,
  memberId,
  carrierId,
  carrierName,
  canNavigate = false,
}: {
  policyId: string
  effectiveDate?: string | Date | null
  memberId?: string | null
  carrierId?: string | null
  carrierName?: string | null
  canNavigate?: boolean
}) => {
  const parsedPolicyId = String(parseId(policyId))
  const policyEffectiveYear = getEffectiveYear(effectiveDate)
  const navigate = useNavigate()
  const parsedCarrierId = carrierId ? String(parseId(carrierId)) : null

  const buildReportUrl = (row: CommissionRow) => {
    if (row.memberId) {
      return `/commissions/${row.reportId}/details?memberId=${encodeURIComponent(row.memberId)}`
    }

    const clientSegment = row.client ? `/${encodeURIComponent(row.client)}` : ''
    return `/commissions/${row.reportId}/details${clientSegment}`
  }

  const {
    data: detailsData,
    loading: loadingByPolicy,
    error: errorByPolicy,
  } = useQuery<CommissionDetailsResponse>(GET_POLICY_COMMISSION_REPORTS_DETAILS, {
    variables: {policyId: parsedPolicyId},
    skip: !parsedPolicyId,
    fetchPolicy: 'no-cache',
  })

  const {
    data: detailsByMemberData,
    loading: loadingByMember,
    error: errorByMember,
  } = useQuery<CommissionDetailsByMemberResponse>(GET_MEMBER_COMMISSION_REPORTS_DETAILS, {
    variables: {memberId: memberId || ''},
    skip: !memberId,
    fetchPolicy: 'no-cache',
  })

  if (loadingByPolicy || loadingByMember) {
    return (
      <div className='cardContainer d-flex align-items-center justify-content-center py-5'>
        <Spinner animation='border' />
      </div>
    )
  }

  if (errorByPolicy || errorByMember) {
    return (
      <div className='cardContainer'>
        <Alert variant='danger' className='mb-0'>
          {(errorByPolicy || errorByMember)?.message || 'Unable to load commissions.'}
        </Alert>
      </div>
    )
  }

  const detailMap = new Map<string, CommissionDetailNode>()

  for (const detail of detailsData?.commissionReportsDetailsFiltered || []) {
    if (detail.commissionReport?.status !== 'COMPLETED') {
      continue
    }

    if (policyEffectiveYear && detail.effectiveYear && detail.effectiveYear !== policyEffectiveYear) {
      continue
    }

    detailMap.set(detail.id, detail)
  }

  for (const edge of detailsByMemberData?.allCommissionReportsDetails?.edges || []) {
    const detail = edge.node
    if (detail.commissionReport?.status !== 'COMPLETED') {
      continue
    }

    if (policyEffectiveYear && detail.effectiveYear && detail.effectiveYear !== policyEffectiveYear) {
      continue
    }

    const detailCarrierId = detail.commissionReport?.carrier?.id
      ? String(parseId(detail.commissionReport.carrier.id))
      : null
    const detailCarrierName = detail.commissionReport?.carrier?.name || ''
    const carrierMatchesById = parsedCarrierId && detailCarrierId && detailCarrierId === parsedCarrierId
    const carrierMatchesByName = carriersLookCompatible(carrierName, detailCarrierName)

    if (
      (parsedCarrierId || carrierName) &&
      !carrierMatchesById &&
      !carrierMatchesByName &&
      detailCarrierId
    ) {
      continue
    }

    detailMap.set(detail.id, detail)
  }

  const mergedDetails = Array.from(detailMap.values())

  const rowsByReport = new Map<string, CommissionRow>()

  const ensureRow = (report: {
    id: string
    name: string
    effectiveMonth: number
    effectiveYear: number
  }) => {
    const existing = rowsByReport.get(report.id)
    if (existing) return existing

    const created: CommissionRow = {
      reportId: report.id,
      reportName: report.name,
      reportMonth: report.effectiveMonth || 0,
      reportYear: report.effectiveYear || 0,
      client: null,
      memberId: null,
      cells: {},
    }
    rowsByReport.set(report.id, created)
    return created
  }

  for (const detail of mergedDetails) {
    const row = ensureRow(detail.commissionReport)
    if (!row.client && detail.client) row.client = detail.client
    if (!row.memberId && detail.memberId) row.memberId = detail.memberId

    mergeCell(
      row,
      normalizeMonth(detail.effectiveMonth),
      Number(detail.members || 0),
      resolveDetailStatus(detail.commissionType, detail.status)
    )
  }

  const rows = Array.from(rowsByReport.values()).sort((a, b) => {
    if (a.reportYear !== b.reportYear) return a.reportYear - b.reportYear
    if (a.reportMonth !== b.reportMonth) return a.reportMonth - b.reportMonth
    return a.reportName.localeCompare(b.reportName)
  })

  if (rows.length === 0 && policyEffectiveYear) {
    rows.push({
      reportId: `policy-effective-year-${policyEffectiveYear}`,
      reportName: `Policy effective year ${policyEffectiveYear}`,
      reportMonth: 0,
      reportYear: policyEffectiveYear,
      cells: {},
    })
  }

  const totalsByMonth = MONTH_HEADERS.reduce<Record<number, CommissionCell | undefined>>(
    (acc, _, index) => {
      acc[index] = undefined
      return acc
    },
    {}
  )

  for (const row of rows) {
    for (const [monthKey, cell] of Object.entries(row.cells)) {
      const month = Number(monthKey)
      const current = totalsByMonth[month]

      if (!current) {
        totalsByMonth[month] = {...cell}
        continue
      }

      current.applicants += cell.applicants
      if (STATUS_PRIORITY[cell.status] > STATUS_PRIORITY[current.status]) {
        current.status = cell.status
      }
    }
  }

  return (
    <div className='cardContainer'>
      <div className='d-flex align-items-center gap-2 mb-3'>
        <h4 className='mb-0'>Policy commissions</h4>
      </div>

      <div className='mb-4'>
        <div className='fw-semibold mb-2'>Table information</div>
        <div className='d-flex flex-column gap-1'>
          <div><i className='bi bi-check-circle-fill text-success me-2' style={{fontSize: '1.2rem'}} />Commission paid.</div>
          <div><i className='bi bi-x-circle-fill text-danger me-2' style={{fontSize: '1.2rem'}} />Commission not paid (Not assigned).</div>
          <div><i className='bi bi-dash-circle-fill text-danger me-2' style={{fontSize: '1.2rem'}} />Chargeback.</div>
        </div>
      </div>

      {mergedDetails.length === 0 && (
        <Alert variant='light'>
          Showing the monthly commission calendar for the policy effective year
          {policyEffectiveYear ? ` ${policyEffectiveYear}` : ''}, even though there are no commission details yet.
        </Alert>
      )}

      <div style={{overflowX: 'auto'}}>
        <Table
          responsive
          bordered
          hover
          className='align-middle mb-0'
          style={{tableLayout: 'fixed', width: '100%'}}
        >
          <thead>
            <tr>
              <th style={{width: 220}}>Report</th>
              {MONTH_HEADERS.map((month) => (
                <th
                  key={month}
                  className='text-center px-1'
                  style={{
                    width: month === 'Undefined' ? 78 : 60,
                    fontSize: '0.95rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.reportId}>
                <td style={{wordBreak: 'break-word', fontSize: '0.95rem', overflowWrap: 'anywhere', lineHeight: 1.2}}>
                  {row.reportId.startsWith('policy-effective-year-') || !canNavigate ? (
                    row.reportName
                  ) : (
                    <button
                      type='button'
                      className='btn btn-link p-0 text-start align-baseline'
                      style={{wordBreak: 'break-word', fontSize: '0.95rem', overflowWrap: 'anywhere', lineHeight: 1.2}}
                      onClick={() => navigate(buildReportUrl(row))}
                    >
                      {row.reportName}
                    </button>
                  )}
                </td>
                {MONTH_HEADERS.map((_, monthIndex) => (
                  <td
                    key={`${row.reportId}-${monthIndex}`}
                    className='text-center px-1'
                    style={{whiteSpace: 'nowrap'}}
                  >
                    {row.cells[monthIndex] && !row.reportId.startsWith('policy-effective-year-') && canNavigate ? (
                      <button
                        type='button'
                        className='btn btn-link p-0 text-decoration-none'
                        onClick={() => navigate(buildReportUrl(row))}
                      >
                        <CellContent cell={row.cells[monthIndex]} />
                      </button>
                    ) : (
                      <CellContent cell={row.cells[monthIndex]} />
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr className='table-light fw-semibold'>
              <td>Total applicants paid:</td>
              {MONTH_HEADERS.map((_, monthIndex) => {
                const cell = totalsByMonth[monthIndex]
                return (
                  <td
                    key={`total-${monthIndex}`}
                    className={`text-center px-1 ${formatTotalClassName(cell)}`}
                    style={{whiteSpace: 'nowrap'}}
                  >
                    {cell?.applicants ?? 0}
                  </td>
                )
              })}
            </tr>
          </tbody>
        </Table>
      </div>
    </div>
  )
}
